import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle,
    Bot,
    CheckCircle2,
    FlaskConical,
    Gift,
    KeyRound,
    RefreshCw,
    Save,
    Send,
    ServerCog,
    ShieldCheck,
} from 'lucide-react';

import { API_BASE_URL } from '../../config';
import { formatStarsAmount } from '../../utils/starsCurrency';

const formatTransferUnlock = (unixSeconds) => {
    if (unixSeconds == null || unixSeconds === '') return null;
    const value = Number(unixSeconds);
    if (!Number.isFinite(value) || value <= 0) return null;
    try {
        return new Date(value * 1000).toLocaleString('ru-RU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return null;
    }
};

const RecipientUsernamePicker = ({ value, onChange }) => {
    const [query, setQuery] = useState(value ? `@${value}` : '');
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const rootRef = useRef(null);
    const selectedLabel = useMemo(() => (value ? `@${value}` : ''), [value]);

    useEffect(() => {
        if (value) setQuery(`@${value}`);
    }, [value]);

    useEffect(() => {
        const onDocClick = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    useEffect(() => {
        const raw = String(query || '').trim().replace(/^@/, '');
        if (raw.length < 2) {
            setItems([]);
            setError('');
            return undefined;
        }
        if (value && raw === value) {
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch(
                    `${API_BASE_URL}/api/settings/roulette/gift-delivery/operator-gifts/recipients?q=${encodeURIComponent(raw)}&limit=20`,
                    { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
                );
                const body = await response.json().catch(() => ({}));
                if (!response.ok) throw new Error(body.detail || 'Не удалось найти пользователей');
                if (!cancelled) {
                    setItems(Array.isArray(body.items) ? body.items : []);
                    setOpen(true);
                }
            } catch (searchError) {
                if (!cancelled) {
                    setItems([]);
                    setError(searchError.message || 'Ошибка поиска');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 280);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [query, value]);

    return (
        <div ref={rootRef} className="relative block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Получатель (поиск по базе)</span>
            <input
                className={inputClass}
                value={query}
                onChange={(event) => {
                    setQuery(event.target.value);
                    if (value) onChange('');
                }}
                onFocus={() => {
                    if (items.length) setOpen(true);
                }}
                placeholder="Начните вводить @username"
                autoComplete="off"
            />
            {selectedLabel ? (
                <div className="mt-1.5 text-xs text-emerald-700">Выбран: {selectedLabel}</div>
            ) : (
                <div className="mt-1.5 text-xs text-slate-400">Только пользователи Starspay с Telegram username</div>
            )}
            {error ? <div className="mt-1 text-xs text-rose-600">{error}</div> : null}
            {open && (loading || items.length > 0 || String(query).trim().length >= 2) ? (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {loading ? (
                        <div className="px-3 py-2 text-xs text-slate-500">Ищем…</div>
                    ) : items.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-500">Никого не найдено</div>
                    ) : (
                        items.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className="flex w-full flex-col gap-0.5 border-b border-slate-50 px-3 py-2 text-left last:border-b-0 hover:bg-indigo-50"
                                onClick={() => {
                                    onChange(item.username);
                                    setQuery(`@${item.username}`);
                                    setOpen(false);
                                }}
                            >
                                <span className="text-sm font-semibold text-slate-900">@{item.username}</span>
                                <span className="text-[11px] text-slate-500">
                                    {[item.fio, item.tg_id ? `tg:${item.tg_id}` : null].filter(Boolean).join(' · ')}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            ) : null}
        </div>
    );
};

const DEFAULTS = {
    enabled: false,
    dry_run: true,
    max_attempts: 3,
    telegram_enabled: false,
    telegram_use_main_bot: false,
    telegram_account_label: '',
    telegram_max_stars: 0,
    telegram_pay_for_upgrade: false,
    telegram_message: 'Спасибо за участие в рулетке StarsPay!',
    collectible_enabled: false,
    collectible_purchase_enabled: false,
    collectible_account_label: '',
    collectible_max_price_stars: 0,
    collectible_max_daily_spend_stars: 0,
    collectible_balance_reserve_stars: 0,
    collectible_max_topup_stars: 0,
    collectible_fallback_prize_code: '',
    collectible_allowed_collections: '',
};

const EMPTY_SECRETS = {
    telegram_bot_token: '',
    collectible_operator_session: '',
    collectible_business_connection_id: '',
    clear_telegram_bot_token: false,
    clear_collectible_operator_session: false,
    clear_collectible_business_connection_id: false,
};

const inputClass = 'h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-400';

const Toggle = ({ checked, onChange, label, disabled = false }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? 'bg-indigo-600' : 'bg-slate-300'}`}
    >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? 'left-6' : 'left-1'}`} />
    </button>
);

const StatusBadge = ({ status }) => {
    const configured = Boolean(status?.configured);
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${configured ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {configured ? <CheckCircle2 size={13} /> : <KeyRound size={13} />}
            {configured ? `Настроено: ${status.source === 'environment' || status.source === 'main_bot_environment' ? 'окружение' : 'защищённая БД'}` : 'Не настроено'}
        </span>
    );
};

const GiftDeliverySettingsPanel = ({ onShowToast }) => {
    const [settings, setSettings] = useState(DEFAULTS);
    const [credentials, setCredentials] = useState({});
    const [warnings, setWarnings] = useState([]);
    const [secrets, setSecrets] = useState(EMPTY_SECRETS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [probing, setProbing] = useState('');
    const [probeResult, setProbeResult] = useState(null);
    const [error, setError] = useState('');
    const [operatorGifts, setOperatorGifts] = useState([]);
    const [operatorGiftsTotal, setOperatorGiftsTotal] = useState(null);
    const [operatorStarBalance, setOperatorStarBalance] = useState(null);
    const [operatorStarBalanceError, setOperatorStarBalanceError] = useState('');
    const [businessConnection, setBusinessConnection] = useState(null);
    const [businessConnectionError, setBusinessConnectionError] = useState('');
    const [operatorAccount, setOperatorAccount] = useState(null);
    const [operatorAccountError, setOperatorAccountError] = useState('');
    const [loadingGifts, setLoadingGifts] = useState(false);
    const [transferringId, setTransferringId] = useState('');
    const [transferForm, setTransferForm] = useState({
        owned_gift_id: '',
        username: '',
        star_count: '',
        note: '',
    });

    const applyPayload = (payload) => {
        setSettings({ ...DEFAULTS, ...(payload?.settings || {}) });
        setCredentials(payload?.credentials || {});
        setWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
    };

    const load = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-delivery`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить автовыдачу');
            applyPayload(body);
        } catch (loadError) {
            setError(loadError.message || 'Не удалось загрузить автовыдачу');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));
    const updateSecret = (key, value) => setSecrets((current) => ({ ...current, [key]: value }));

    const save = async () => {
        try {
            setSaving(true);
            setError('');
            setProbeResult(null);
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-delivery`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings, secrets }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : body.detail?.message || 'Не удалось сохранить автовыдачу');
            applyPayload(body);
            setSecrets(EMPTY_SECRETS);
            onShowToast?.('Настройки автовыдачи сохранены');
        } catch (saveError) {
            setError(saveError.message || 'Не удалось сохранить автовыдачу');
        } finally {
            setSaving(false);
        }
    };

    const loadOperatorGifts = async () => {
        try {
            setLoadingGifts(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-delivery/operator-gifts`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить подарки оператора');
            setOperatorGifts(Array.isArray(body.items) ? body.items : []);
            setOperatorGiftsTotal(body.total_count ?? null);
            setOperatorStarBalance(
                body.star_balance && typeof body.star_balance === 'object'
                    ? body.star_balance
                    : null,
            );
            setOperatorStarBalanceError(body.star_balance_error || '');
            setBusinessConnection(
                body.business_connection && typeof body.business_connection === 'object'
                    ? body.business_connection
                    : null,
            );
            setBusinessConnectionError(body.business_connection_error || '');
            setOperatorAccount(body.operator && typeof body.operator === 'object' ? body.operator : null);
            setOperatorAccountError(body.operator_error || '');
            if (onShowToast) onShowToast(`Загружено подарков: ${body.loaded ?? 0}`);
        } catch (loadError) {
            setError(loadError.message || 'Не удалось загрузить подарки оператора');
        } finally {
            setLoadingGifts(false);
        }
    };

    const transferOperatorGift = async () => {
        const ownedGiftId = String(transferForm.owned_gift_id || '').trim();
        const username = String(transferForm.username || '').trim().replace(/^@/, '');
        if (!ownedGiftId) {
            setError('Выберите подарок или укажите owned_gift_id');
            return;
        }
        if (!username) {
            setError('Выберите получателя из поиска по базе');
            return;
        }
        const payload = {
            owned_gift_id: ownedGiftId,
            username,
            star_count: transferForm.star_count === '' ? null : Number(transferForm.star_count),
            note: String(transferForm.note || '').trim() || null,
        };
        try {
            setTransferringId(ownedGiftId);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-delivery/operator-gifts/transfer`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось отправить подарок');
            if (onShowToast) {
                onShowToast(`Подарок отправлен → @${body.recipient?.username || username}`);
            }
            setTransferForm((current) => ({ ...current, owned_gift_id: '', note: '' }));
            await loadOperatorGifts();
        } catch (transferError) {
            setError(transferError.message || 'Не удалось отправить подарок');
        } finally {
            setTransferringId('');
        }
    };

    const probe = async (provider) => {
        try {
            setProbing(provider);
            setError('');
            setProbeResult(null);
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/gift-delivery/probe`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : body.detail?.message || 'Провайдер не ответил');
            setProbeResult(body);
            const labels = { telegram: 'Telegram', collectible: 'Collectible Marketplace' };
            onShowToast?.(`Связь с ${labels[provider] || provider} проверена`);
        } catch (probeError) {
            setError(probeError.message || 'Не удалось проверить провайдера');
        } finally {
            setProbing('');
        }
    };

    if (loading) return <div className="py-16 text-center text-sm text-slate-500">Загрузка автовыдачи...</div>;

    return (
        <div className="space-y-5">
            {error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
            )}

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-600"><ServerCog size={20} /></div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Автоматическая выдача подарков</h3>
                            <p className="mt-1 text-sm leading-5 text-slate-500">Отдельный выключатель не влияет на саму рулетку.</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-4 sm:justify-end">
                        <span className={`text-sm font-semibold ${settings.enabled ? 'text-emerald-700' : 'text-slate-500'}`}>{settings.enabled ? 'Включена' : 'Выключена'}</span>
                        <Toggle checked={settings.enabled} onChange={(value) => update('enabled', value)} label="Включить автовыдачу" />
                    </div>
                </div>
                <div className={`flex flex-col gap-4 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${settings.dry_run ? 'border-sky-100 bg-sky-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="flex gap-3">
                        {settings.dry_run ? <FlaskConical className="mt-0.5 shrink-0 text-sky-700" size={18} /> : <AlertTriangle className="mt-0.5 shrink-0 text-amber-700" size={18} />}
                        <div>
                            <div className={`text-sm font-semibold ${settings.dry_run ? 'text-sky-900' : 'text-amber-900'}`}>{settings.dry_run ? 'Тестовый режим' : 'Live-выдача'}</div>
                            <p className={`mt-1 text-xs leading-5 ${settings.dry_run ? 'text-sky-700' : 'text-amber-800'}`}>
                                {settings.dry_run
                                    ? 'API не вызываются: приз помечается выданным в БД и ЛК (имитация). На APP_ENV=development live API всегда выключены.'
                                    : 'После выигрыша сервис может списать Stars (XTR) и отправить gift через Bot API / Marketplace.'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-slate-600">Dry-run</span>
                        <Toggle checked={settings.dry_run} onChange={(value) => update('dry_run', value)} label="Тестовый режим" />
                    </div>
                </div>
                <div className="grid gap-3 border-t border-slate-100 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center sm:px-6">
                    <div>
                        <div className="text-sm font-semibold text-slate-800">Автоматические повторы</div>
                        <p className="mt-1 text-xs leading-5 text-slate-500">Только для однозначных ошибок до покупки. Неопределённый результат всегда требует ручной проверки.</p>
                    </div>
                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">Попыток, максимум</span>
                        <input type="number" min="1" max="5" className={inputClass} value={settings.max_attempts} onChange={(event) => update('max_attempts', Number(event.target.value || 1))} />
                    </label>
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-700"><Bot size={20} /></div>
                        <div><h3 className="font-semibold text-slate-900">Telegram Bot API</h3><p className="mt-1 text-sm text-slate-500">Обычные подарки оплачиваются с баланса Stars (XTR) бота.</p></div>
                    </div>
                    <div className="flex items-center gap-3"><StatusBadge status={credentials.telegram_bot_token} /><Toggle checked={settings.telegram_enabled} onChange={(value) => update('telegram_enabled', value)} label="Включить Telegram" /></div>
                </div>
                <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-2">
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Подпись аккаунта</span><input className={inputClass} value={settings.telegram_account_label} onChange={(event) => update('telegram_account_label', event.target.value)} placeholder="Например: основной бот" /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Лимит на один подарок, Stars (XTR)</span><input type="number" min="0" className={inputClass} value={settings.telegram_max_stars} onChange={(event) => update('telegram_max_stars', Number(event.target.value || 0))} /></label>
                    <label className="block lg:col-span-2"><span className="mb-2 block text-sm font-medium text-slate-700">Отдельный токен бота</span><input type="password" autoComplete="new-password" className={inputClass} disabled={settings.telegram_use_main_bot || secrets.clear_telegram_bot_token} value={secrets.telegram_bot_token} onChange={(event) => updateSecret('telegram_bot_token', event.target.value)} placeholder={credentials.telegram_custom_token?.configured ? 'Сохранён; введите новый для замены' : '123456:ABC...'} /></label>
                    {credentials.telegram_custom_token?.configured && (
                        <label className="flex items-center gap-3 text-sm text-slate-600 lg:col-span-2"><input type="checkbox" className="h-4 w-4 accent-rose-600" checked={secrets.clear_telegram_bot_token} onChange={(event) => updateSecret('clear_telegram_bot_token', event.target.checked)} />Удалить сохранённый отдельный токен при сохранении</label>
                    )}
                    <div className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 px-4 py-3 lg:col-span-2">
                        <div><div className="text-sm font-semibold text-slate-800">Использовать основной BOT_TOKEN</div><p className="mt-1 text-xs leading-5 text-slate-500">Токен берётся из окружения и не копируется в БД.</p></div>
                        <Toggle checked={settings.telegram_use_main_bot} onChange={(value) => update('telegram_use_main_bot', value)} label="Использовать основной бот" />
                    </div>
                    <label className="block lg:col-span-2"><span className="mb-2 block text-sm font-medium text-slate-700">Текст к подарку</span><input className={inputClass} maxLength={128} value={settings.telegram_message} onChange={(event) => update('telegram_message', event.target.value)} /></label>
                    <label className="flex items-center gap-3 text-sm font-medium text-slate-700"><input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={settings.telegram_pay_for_upgrade} onChange={(event) => update('telegram_pay_for_upgrade', event.target.checked)} />Оплачивать upgrade подарка</label>
                    <div className="flex flex-col items-end gap-1"><button type="button" onClick={() => probe('telegram')} disabled={probing === 'telegram'} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"><RefreshCw size={16} className={probing === 'telegram' ? 'animate-spin' : ''} />Проверить баланс</button><span className="text-[11px] text-slate-400">Новые реквизиты сначала сохраните</span></div>
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-violet-50 text-violet-700"><ShieldCheck size={20} /></div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Коллекционные gifts (Marketplace)</h3>
                            <p className="mt-1 text-sm text-slate-500">
                                Marketplace Stars (XTR): оператор оплачивает покупку, подарок сразу уходит победителю (to_id), без склада и 21-дневного transfer-холда.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <StatusBadge status={credentials.collectible_operator_session} />
                        <Toggle checked={settings.collectible_enabled} onChange={(value) => update('collectible_enabled', value)} label="Включить collectible" />
                    </div>
                </div>
                <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-2">
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Подпись аккаунта</span><input className={inputClass} value={settings.collectible_account_label} onChange={(event) => update('collectible_account_label', event.target.value)} placeholder="Операторский аккаунт Marketplace" /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Лимит на один подарок, Stars (XTR)</span><input type="number" min="0" className={inputClass} value={settings.collectible_max_price_stars} onChange={(event) => update('collectible_max_price_stars', Number(event.target.value || 0))} /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Лимит за сутки, Stars (XTR)</span><input type="number" min="0" className={inputClass} value={settings.collectible_max_daily_spend_stars} onChange={(event) => update('collectible_max_daily_spend_stars', Number(event.target.value || 0))} /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Неснижаемый остаток Stars (XTR)</span><input type="number" min="0" className={inputClass} value={settings.collectible_balance_reserve_stars} onChange={(event) => update('collectible_balance_reserve_stars', Number(event.target.value || 0))} /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Макс. автопополнение Stars (XTR)</span><input type="number" min="0" className={inputClass} value={settings.collectible_max_topup_stars} onChange={(event) => update('collectible_max_topup_stars', Number(event.target.value || 0))} /></label>
                    <label className="block"><span className="mb-2 block text-sm font-medium text-slate-700">Fallback prize code</span><input className={inputClass} value={settings.collectible_fallback_prize_code} onChange={(event) => update('collectible_fallback_prize_code', event.target.value)} placeholder="код приза при отсутствии лота" /></label>
                    <label className="block lg:col-span-2"><span className="mb-2 block text-sm font-medium text-slate-700">Разрешённые коллекции (через запятую)</span><input className={inputClass} value={settings.collectible_allowed_collections} onChange={(event) => update('collectible_allowed_collections', event.target.value)} placeholder="Loot Bag, Desk Calendar" /></label>
                    <label className="block lg:col-span-2"><span className="mb-2 block text-sm font-medium text-slate-700">Operator session (зашифруется)</span><textarea rows="3" disabled={secrets.clear_collectible_operator_session} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 font-mono text-xs text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50" value={secrets.collectible_operator_session} onChange={(event) => updateSecret('collectible_operator_session', event.target.value)} placeholder={credentials.collectible_operator_session?.configured ? 'Сохранена; вставьте новую для замены' : 'TELEGRAM_OPERATOR_SESSION'} /></label>
                    {credentials.collectible_operator_session?.configured && (
                        <label className="flex items-center gap-3 text-sm text-slate-600 lg:col-span-2"><input type="checkbox" className="h-4 w-4 accent-rose-600" checked={secrets.clear_collectible_operator_session} onChange={(event) => updateSecret('clear_collectible_operator_session', event.target.checked)} />Удалить session при сохранении</label>
                    )}
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 lg:col-span-2">
                        Business connection ID для collectible не нужен: покупка идёт через session оператора сразу на победителя.
                        Ручной склад/transfer ниже — для заранее купленных gifts и отладки.
                    </div>
                    <label className="block lg:col-span-2"><span className="mb-2 block text-sm font-medium text-slate-700">Business connection ID (не используется для transfer)</span><input type="password" autoComplete="new-password" disabled={secrets.clear_collectible_business_connection_id} className={inputClass} value={secrets.collectible_business_connection_id} onChange={(event) => updateSecret('collectible_business_connection_id', event.target.value)} placeholder={credentials.collectible_business_connection_id?.configured ? 'Сохранён; введите новый' : 'не обязателен'} /></label>
                    {credentials.collectible_business_connection_id?.configured && (
                        <label className="flex items-center gap-3 text-sm text-slate-600 lg:col-span-2"><input type="checkbox" className="h-4 w-4 accent-rose-600" checked={secrets.clear_collectible_business_connection_id} onChange={(event) => updateSecret('clear_collectible_business_connection_id', event.target.checked)} />Удалить Business connection ID при сохранении</label>
                    )}
                    <div className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 px-4 py-3 lg:col-span-2">
                        <div><div className="text-sm font-semibold text-slate-800">Разрешить покупку (kill-switch)</div><p className="mt-1 text-xs leading-5 text-slate-500">Выключено — только dry-run/валидация, без purchase/transfer.</p></div>
                        <Toggle checked={settings.collectible_purchase_enabled} onChange={(value) => update('collectible_purchase_enabled', value)} label="Разрешить покупку collectible" />
                    </div>
                    <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        Автопополнение Stars (XTR) оператора через Tonkeeper настраивается в{' '}
                        <span className="font-medium text-slate-800">Настройки бота</span>
                        {' '}(по умолчанию выкл.). Работает для обычных подарков, Premium и коллекционных с оператора.
                    </p>
                    <div className="flex flex-col items-end gap-1"><button type="button" onClick={() => probe('collectible')} disabled={probing === 'collectible'} className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"><RefreshCw size={16} className={probing === 'collectible' ? 'animate-spin' : ''} />Проверить готовность</button><span className="text-[11px] text-slate-400">Проверяет session оператора и список его gifts</span></div>
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex gap-3">
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700"><Gift size={20} /></div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Подарки оператора (session)</h3>
                            <p className="mt-1 text-sm text-slate-500">
                                Список unique gifts на аккаунте оператора и ручной transfer через session по username / tg_id.
                                Stars (XTR) за платный transfer списываются с оператора.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-right">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Оператор</div>
                            <div className="mt-0.5 text-sm font-semibold text-slate-900">
                                {operatorAccount?.username
                                    ? `@${operatorAccount.username}`
                                    : operatorAccount?.id
                                        ? `id ${operatorAccount.id}`
                                        : loadingGifts
                                            ? '…'
                                            : '—'}
                            </div>
                            {operatorStarBalance?.amount_stars != null || operatorStarBalance?.balance != null ? (
                                <div className="mt-1 text-xs font-semibold text-violet-700">
                                    {formatStarsAmount(
                                        operatorStarBalance.amount_stars ?? operatorStarBalance.balance,
                                    )}
                                </div>
                            ) : null}
                            {operatorAccountError || operatorStarBalanceError ? (
                                <div className="mt-1 max-w-[260px] text-[11px] leading-4 text-amber-700">
                                    {operatorAccountError || operatorStarBalanceError}
                                </div>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={loadOperatorGifts}
                            disabled={loadingGifts}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50"
                        >
                            <RefreshCw size={16} className={loadingGifts ? 'animate-spin' : ''} />
                            {loadingGifts ? 'Загрузка…' : 'Обновить список'}
                        </button>
                    </div>
                </div>

                <div className="grid gap-5 border-b border-slate-100 p-5 sm:p-6 lg:grid-cols-2">
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">slug / owned_gift_id</span>
                        <input
                            className={inputClass}
                            value={transferForm.owned_gift_id}
                            onChange={(event) => setTransferForm((current) => ({ ...current, owned_gift_id: event.target.value }))}
                            placeholder="BigYear-18394 или выберите из списка"
                        />
                    </label>
                    <RecipientUsernamePicker
                        value={transferForm.username}
                        onChange={(username) => setTransferForm((current) => ({ ...current, username }))}
                    />
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">Stars (XTR) за transfer (если платный)</span>
                        <input
                            type="number"
                            min="0"
                            className={inputClass}
                            value={transferForm.star_count}
                            onChange={(event) => setTransferForm((current) => ({ ...current, star_count: event.target.value }))}
                            placeholder="из списка / 0"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-slate-700">Комментарий</span>
                        <input
                            className={inputClass}
                            value={transferForm.note}
                            onChange={(event) => setTransferForm((current) => ({ ...current, note: event.target.value }))}
                            placeholder="Ручная выдача после ошибки рулетки"
                        />
                    </label>
                    <div className="lg:col-span-2">
                        <button
                            type="button"
                            onClick={transferOperatorGift}
                            disabled={Boolean(transferringId)}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-violet-600 px-5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                            <Send size={17} />
                            {transferringId ? 'Отправка…' : 'Отправить выбранный подарок'}
                        </button>
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                            Transfer идёт с session оператора по @username из базы. Пользователей без username отправить нельзя. Для авторулетки желательна активность в боте за 24 ч.
                        </p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {operatorGifts.length === 0 ? (
                        <div className="px-5 py-8 text-center text-sm text-slate-500">
                            {loadingGifts ? 'Загрузка…' : 'Список пуст. Нажмите «Обновить список» после настройки session оператора.'}
                        </div>
                    ) : (
                        <table className="w-full min-w-[1040px] border-collapse text-left">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50 text-xs uppercase text-slate-500">
                                    <th className="px-4 py-3 font-semibold sm:pl-5">Подарок</th>
                                    <th className="px-4 py-3 font-semibold">slug</th>
                                    <th className="px-4 py-3 font-semibold">Transfer, Stars (XTR)</th>
                                    <th className="px-4 py-3 font-semibold">Статус</th>
                                    <th className="px-4 py-3 font-semibold">Можно с</th>
                                    <th className="px-4 py-3 font-semibold"><span className="sr-only">Действие</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {operatorGifts.map((gift) => (
                                    <tr key={gift.owned_gift_id} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-3 sm:pl-5">
                                            <div className="text-sm font-semibold text-slate-900">
                                                {gift.name || gift.base_name || gift.title || 'Подарок'}
                                            </div>
                                            <div className="mt-1 text-xs text-slate-500">
                                                {[gift.model, gift.backdrop, gift.symbol, gift.number ? `#${gift.number}` : null]
                                                    .filter(Boolean)
                                                    .join(' · ') || 'без атрибутов'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{gift.owned_gift_id}</td>
                                        <td className="px-4 py-3 text-sm text-slate-700">{formatStarsAmount(gift.transfer_star_count || 0)}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            {gift.can_be_transferred === false ? 'Нельзя сейчас' : 'Можно передать'}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500">
                                            {gift.can_be_transferred === false
                                                ? (formatTransferUnlock(gift.next_transfer_date) || '—')
                                                : 'сейчас'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700"
                                                onClick={() => setTransferForm((current) => ({
                                                    ...current,
                                                    owned_gift_id: gift.owned_gift_id,
                                                    star_count: gift.transfer_star_count ? String(gift.transfer_star_count) : '',
                                                }))}
                                            >
                                                Выбрать
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {operatorGiftsTotal != null && (
                    <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
                        Показано {operatorGifts.length} из ~{operatorGiftsTotal}
                    </div>
                )}
            </section>

            {warnings.map((warning) => <div key={warning} className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-800"><AlertTriangle size={17} className="mt-0.5 shrink-0" />{warning}</div>)}

            {probeResult && (
                <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex gap-3"><ShieldCheck size={20} className="shrink-0 text-emerald-700" /><div><h3 className="font-semibold text-emerald-950">Соединение работает</h3><pre className="mt-3 whitespace-pre-wrap break-words text-xs leading-5 text-emerald-900">{JSON.stringify(probeResult, null, 2)}</pre></div></div>
                </section>
            )}

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck size={16} />Таймаут покупки всегда уходит на ручную проверку.</div>
                <button type="button" onClick={save} disabled={saving} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"><Save size={18} />{saving ? 'Сохранение...' : 'Сохранить автовыдачу'}</button>
            </div>
        </div>
    );
};

export default GiftDeliverySettingsPanel;
